/**
 * Clone Project Modal - Clone an existing project
 * Modal form to clone a project with options for including configurations.
 */

import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  HStack,
  Input,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Check } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';

import { ErrorBoundary } from '~/components/ErrorBoundary';
import { ErrorFallback } from '~/components/ErrorBoundary/ErrorFallback';
import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import { useProjectStore } from '~/hooks/useProjectStore';

export interface CloneProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Project to clone */
  project: {
    id: string;
    bondName: string;
    clientName?: string;
  } | null;
}

type CloneMode = 'with-configurations' | 'only-setup';

// Helper component for clone mode selection
const CloneModeOption: React.FC<{
  value: string;
  label: string;
  description: string;
  isSelected: boolean;
  onClick: () => void;
}> = ({ label, description, isSelected, onClick }) => (
  <Box
    as="button"
    onClick={onClick}
    p={3}
    border="2px solid"
    borderColor={isSelected ? 'blue.500' : 'gray.200'}
    borderRadius={LAYOUT_SPACING.BORDER_RADIUS}
    bg={isSelected ? 'blue.50' : 'white'}
    textAlign="left"
    cursor="pointer"
    _hover={{ borderColor: isSelected ? 'blue.500' : 'gray.300' }}
    transition="all 0.2s"
  >
    <HStack justify="space-between" align="flex-start">
      <VStack align="start" gap={0}>
        <Text fontWeight="medium" color={isSelected ? 'blue.700' : 'gray.800'}>
          {label}
        </Text>
        <Text fontSize="sm" color="gray.600">
          {description}
        </Text>
      </VStack>
      {isSelected && (
        <Box color="blue.500" flexShrink={0}>
          <Check size={20} />
        </Box>
      )}
    </HStack>
  </Box>
);

export const CloneProjectModal: React.FC<CloneProjectModalProps> = ({
  isOpen,
  onClose,
  project,
}) => {
  const cloneProject = useProjectStore((state) => state.cloneProject);

  // Form state
  const [newName, setNewName] = useState('');
  const [cloneMode, setCloneMode] = useState<CloneMode>('with-configurations');
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && project) {
      setNewName(`${project.bondName} Copy`);
      setCloneMode('with-configurations');
      setErrors([]);
    }
  }, [isOpen, project]);

  // Check if form is valid
  const isFormValid = newName.trim() !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!project) return;

    // Validate
    const newErrors: string[] = [];
    if (!newName.trim()) {
      newErrors.push('Project name is required');
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const newProjectId = await cloneProject(project.id, {
        newName: newName.trim(),
        includeConfigurations: cloneMode === 'with-configurations',
      });

      if (!newProjectId) {
        setErrors(['Failed to clone project. Please try again.']);
        setIsSubmitting(false);
        return;
      }

      onClose();
    } catch {
      setErrors(['Failed to clone project. Please try again.']);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!project) return null;

  return (
    <ErrorBoundary fallback={<ErrorFallback section="clone project form" />}>
      <Dialog.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()} placement="center">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxWidth={LAYOUT_SPACING.MAX_WIDTH_FORM}>
            <Dialog.Header>
              <Dialog.Title>Clone Project</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <VStack gap={LAYOUT_SPACING.SECTION_GAP} align="stretch">
                {/* Description */}
                <Text color="gray.600" fontSize="sm">
                  Create a copy of "{project.bondName}". You can choose to include existing
                  configurations or start fresh with only the setup data.
                </Text>

                {/* Error Display */}
                {errors.length > 0 && (
                  <Box
                    bg="red.50"
                    border="1px solid"
                    borderColor="red.200"
                    borderRadius={LAYOUT_SPACING.BORDER_RADIUS}
                    p={LAYOUT_SPACING.CARD_PADDING}
                  >
                    <Text color="red.700" fontWeight="medium" mb={LAYOUT_SPACING.ELEMENT_GAP}>
                      Please fix the following errors:
                    </Text>
                    <VStack align="start" gap={1}>
                      {errors.map((error) => (
                        <Text key={error} color="red.600" fontSize="sm">
                          • {error}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} id="clone-project-form">
                  <Stack gap={LAYOUT_SPACING.CONTENT_GAP}>
                    {/* New Project Name */}
                    <Field.Root required>
                      <Field.Label>New Project Name</Field.Label>
                      <Input
                        value={newName}
                        onChange={(e) => {
                          setNewName(e.target.value);
                          setErrors([]);
                        }}
                        placeholder="Enter a name for the cloned project"
                        autoFocus
                      />
                      <Field.HelperText>A unique name for the cloned project</Field.HelperText>
                    </Field.Root>

                    {/* Clone Mode */}
                    <Field.Root>
                      <Field.Label>What to Include</Field.Label>
                      <VStack gap={2} align="stretch" mt={2}>
                        <CloneModeOption
                          value="with-configurations"
                          label="With Configurations"
                          description="Copy all project setup data plus existing configurations and candidates"
                          isSelected={cloneMode === 'with-configurations'}
                          onClick={() => setCloneMode('with-configurations')}
                        />
                        <CloneModeOption
                          value="only-setup"
                          label="Only Setup"
                          description="Copy only the project setup data (dates, tax values, millage, DRF) without configurations"
                          isSelected={cloneMode === 'only-setup'}
                          onClick={() => setCloneMode('only-setup')}
                        />
                      </VStack>
                    </Field.Root>
                  </Stack>
                </form>
              </VStack>
            </Dialog.Body>

            <Dialog.Footer>
              <HStack justify="flex-end" gap={LAYOUT_SPACING.BUTTON_GAP_LG} w="full">
                <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorPalette="blue"
                  form="clone-project-form"
                  disabled={!isFormValid || isSubmitting}
                  loading={isSubmitting}
                >
                  Clone Project
                </Button>
              </HStack>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </ErrorBoundary>
  );
};
